import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X, Send, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";
import api from "@/lib/api";

function formatMessageContent(text) {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((line, li) => {
    const cleanLine = line.replace(/(^|\s)#{1,4}(?=\s|$)/g, "$1");
    const parts = cleanLine.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return (
      <React.Fragment key={li}>
        {parts.map((part, pi) =>
          part.startsWith("**") && part.endsWith("**") ? (
            <strong key={pi} className="font-semibold text-white">{part.slice(2, -2)}</strong>
          ) : (
            <span key={pi}>{part}</span>
          )
        )}
        {li < lines.length - 1 && <br />}
      </React.Fragment>
    );
  });
}

async function consumeMentorStream(response, onChunk) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const part of parts) {
      if (!part.startsWith("data: ")) continue;
      const content = part.slice(6);
      if (content === "[DONE]") continue;
      onChunk(content);
    }
  }
}

const QUICK_ACTIONS = [
  { label: "Explain an error", template: "I'm getting this error, can you explain what it means and how to fix it: " },
  { label: "Generate SQL", template: "Generate a SQL query that " },
  { label: "Formula help", template: "Help me write a Data Cloud Calculated Insight formula that " },
  { label: "Compare concepts", template: "What's the difference between " },
];

export default function AIMentorWidget() {
  const [open, setOpen] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [showTip, setShowTip] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm your AI Mentor on Cloudivo. Ask me anything about Data Streams, Identity Resolution, Segmentation, or any Data Cloud concept." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionIdRef = useRef(`session-${Date.now()}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/nexus/summary").then((res) => {
      setSuggestion(res.data.suggested_action);
      const dismissedKey = `dc30_nexus_tip_dismissed_${res.data.suggested_action.label}`;
      if (!sessionStorage.getItem(dismissedKey)) {
        setShowTip(true);
      }
    }).catch(() => {});
  }, []);

  const dismissTip = () => {
    if (suggestion) sessionStorage.setItem(`dc30_nexus_tip_dismissed_${suggestion.label}`, "1");
    setShowTip(false);
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const appendToLastAssistantMessage = (chunk) => {
    setMessages((prev) => {
      const updated = [...prev];
      updated[updated.length - 1] = { role: "assistant", content: updated[updated.length - 1].content + chunk };
      return updated;
    });
  };

  const sendMessage = async () => {
    if (!input.trim() || streaming) return;
    const userMessage = input;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const response = await fetch(`${API}/ai/mentor/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMessage, session_id: sessionIdRef.current }),
      });
      await consumeMentorStream(response, appendToLastAssistantMessage);
    } catch (e) {
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = { role: "assistant", content: "Sorry, I couldn't respond right now." };
        return updated;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <>
      {!open && showTip && suggestion && (
        <div
          className="fixed bottom-[152px] right-6 z-50 w-60 neu-card rounded-2xl p-3.5 animate-in fade-in slide-in-from-bottom-2"
          data-testid="ai-mentor-nexus-tip"
        >
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1"><Sparkles className="w-2.5 h-2.5 text-[#5b9dff]" /> Career Nexus</span>
            <button onClick={dismissTip} data-testid="ai-mentor-nexus-tip-dismiss-btn" className="text-slate-500 hover:text-white"><X className="w-3 h-3" /></button>
          </div>
          <p className="text-xs text-white font-medium leading-snug">{suggestion.label}</p>
          <Link
            to={suggestion.link} onClick={dismissTip} data-testid="ai-mentor-nexus-tip-link"
            className="mt-2 flex items-center justify-center gap-1 text-[11px] font-semibold text-white bg-gradient-to-r from-[#5b9dff] to-[#b58bff] px-2.5 py-1.5 rounded-lg"
          >
            Let's go <ArrowRight className="w-2.5 h-2.5" />
          </Link>
        </div>
      )}

      {!open && (
        <button
          onClick={() => { setOpen(true); dismissTip(); }}
          data-testid="ai-mentor-open-button"
          className="fixed bottom-24 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] shadow-[0_0_25px_rgba(99,102,241,0.4)] flex items-center justify-center hover:scale-110 transition-transform"
        >
          <Sparkles className="w-6 h-6 text-white" />
          {showTip && suggestion && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#34D399] ring-2 ring-[#0a1120]" data-testid="ai-mentor-nexus-badge" />}
        </button>
      )}

      {open && (
        <div
          className="fixed bottom-24 right-6 z-50 w-[92vw] sm:w-96 h-[520px] glass-panel rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-in fade-in slide-in-from-bottom-4"
          data-testid="ai-mentor-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/5">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-400" />
              <span className="font-heading font-semibold text-sm text-white">AI Mentor</span>
            </div>
            <button onClick={() => setOpen(false)} data-testid="ai-mentor-close-button">
              <X className="w-4 h-4 text-slate-400 hover:text-white" />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto scrollbar-thin p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-primary text-white" : "bg-white/10 text-slate-200"
                  }`}
                  data-testid={`ai-mentor-message-${m.role}-${i}`}
                >
                  {m.content ? formatMessageContent(m.content) : (streaming && i === messages.length - 1 ? "..." : "")}
                </div>
              </div>
            ))}
          </div>

          <div className="px-3 pt-2.5 flex items-center gap-1.5 overflow-x-auto scrollbar-thin" data-testid="ai-mentor-quick-actions">
            {QUICK_ACTIONS.map((qa) => (
              <button
                key={qa.label}
                onClick={() => { setInput(qa.template); inputRef.current?.focus(); }}
                data-testid={`ai-mentor-quick-action-${qa.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="shrink-0 text-[11px] font-semibold px-2.5 py-1.5 rounded-full border border-white/10 text-slate-400 hover:text-indigo-300 hover:border-indigo-400/40 transition-colors"
              >
                {qa.label}
              </button>
            ))}
          </div>

          <div className="p-3 border-t border-white/10 flex items-center gap-2">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Ask about Data Cloud..."
              data-testid="ai-mentor-input"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <button
              onClick={sendMessage}
              disabled={streaming}
              data-testid="ai-mentor-send-button"
              className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center disabled:opacity-50 hover:bg-primary/80 transition-colors"
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
